/**
 * PlaybackError - the single error type for the playback pipeline.
 *
 * Every failure between "user clicked play" and "frames on screen" becomes one
 * of these, so the player always has a human sentence to render. PIN 7: the bug
 * we are fixing is SILENCE — a code with no message is not allowed to exist.
 *
 * The copy for the API-facing codes is verbatim from the PINS error table.
 */

import { isUserSafeMessage } from '../../utils/errorMessages';

/** @typedef {'geo_blocked'|'locked'|'manifest_unavailable'|'not_found'|'not_yet_available'|'device_not_allowed'|'concurrency_limit'|'undetermined'|'unsupported'|'playback_failed'} PlaybackErrorCode */

/** @type {Record<PlaybackErrorCode, string>} */
export const PLAYBACK_ERROR_MESSAGES = {
  // --- PINS error table, verbatim ---
  geo_blocked: "This video isn't available in your region.",
  locked: 'You need an active subscription to watch this.',
  manifest_unavailable: 'This video is temporarily unavailable. Please try again.',
  not_found: 'This video could not be found.',
  // --- v1 /play refusals (bkfc-cms docs/routes/playback.md) ---
  not_yet_available: "This isn't available to watch right now.",
  device_not_allowed: "This video can't be played on this device.",
  concurrency_limit: 'Too many screens are watching right now.',
  // Never a paywall: we could not tell whether the viewer is entitled.
  undetermined: "We couldn't confirm your access right now. Please try again.",
  // --- client-side failures, which the table does not cover ---
  unsupported: "This browser can't play this video.",
  playback_failed: 'Playback stopped unexpectedly. Please try again.',
};

/**
 * An unrecognised code must still render text, so it falls back to the generic
 * "temporarily unavailable" sentence rather than producing an empty message.
 */
export class PlaybackError extends Error {
  /**
   * @param {PlaybackErrorCode} code
   * @param {{cause?: unknown, message?: string}} [options] - `cause` keeps the underlying HTTP /
   *   hls.js error for logging and is never shown; `message` replaces the default copy.
   */
  constructor(code, options = {}) {
    super(
      options.message ||
        PLAYBACK_ERROR_MESSAGES[code] ||
        PLAYBACK_ERROR_MESSAGES.manifest_unavailable,
    );
    this.name = 'PlaybackError';
    this.code = code;
    this.cause = options.cause;
  }
}

const API_TO_PLAYBACK = {
  GEO_BLOCKED: 'geo_blocked',
  NOT_YET_AVAILABLE: 'not_yet_available',
  NOT_PUBLISHED: 'not_yet_available',
  DEVICE_NOT_ALLOWED: 'device_not_allowed',
  CONCURRENCY_LIMIT: 'concurrency_limit',
  NOT_FOUND: 'not_found',
  ENTITLEMENT_UNDETERMINED: 'undetermined',
};

/** The server writes these messages for the viewer (the region, the partner, the limit). */
const SERVER_COPY = ['GEO_BLOCKED', 'NOT_YET_AVAILABLE', 'CONCURRENCY_LIMIT'];

/**
 * A `/play` refusal that is shown in the player. LOGIN_REQUIRED and NOT_ENTITLED never get here:
 * they route to sign-in and the paywall.
 * @param {import('../../api').ApiError | Error} error
 */
export function playbackErrorFromApi(error) {
  const code = API_TO_PLAYBACK[error?.code] || 'manifest_unavailable';
  const message =
    SERVER_COPY.includes(error?.code) && isUserSafeMessage(error.message)
      ? error.message.trim()
      : undefined;
  return new PlaybackError(code, { cause: error, message });
}

export default PlaybackError;
